import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Case-manager rule context ────────────────────────────────────────────────
// Threaded through RenderElement's recursive tree so role-based visibility /
// readonly do not need explicit prop-drilling at every call site.
const RuleContext = React.createContext({ actorRoles: [] });

function _arrIntersects(a, b) {
  if (!Array.isArray(a) || a.length === 0) return false;
  if (!Array.isArray(b) || b.length === 0) return false;
  const set = new Set(a.map(s => String(s).trim()));
  return b.some(v => set.has(String(v).trim()));
}

// Per-field role gates evaluated against the resolving worker's roles.
// Returns { visible, readonly }. Empty arrays = no restriction.
function evaluateCaseManagerRoleRules(elem, actorRoles) {
  const out = { visible: true, readonly: false };
  const vRoles = elem.visible_for_roles;
  if (Array.isArray(vRoles) && vRoles.length > 0 && !_arrIntersects(actorRoles, vRoles)) {
    out.visible = false;
  }
  const rRoles = elem.readonly_for_roles;
  if (Array.isArray(rRoles) && rRoles.length > 0 && _arrIntersects(actorRoles, rRoles)) {
    out.readonly = true;
  }
  return out;
}

// Top-level validator called from CaseManager before submitting a decision.
// Walks the form definition, evaluates required-set against (decision, actorRoles)
// and existing field-required flags, and returns { fieldName: errorMessage } for
// any missing values. Hidden-by-role fields are skipped entirely.
export function validateCaseManagerRules(definition, values, decision, actorRoles) {
  const errors = {};
  const roles = Array.isArray(actorRoles) ? actorRoles : [];
  const decisionStr = String(decision || '').trim();
  const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

  const visit = (elems) => {
    (elems || []).forEach(e => {
      if (e && typeof e === 'object') {
        // Skip hidden-by-role fields — their values aren't collected.
        const { visible } = evaluateCaseManagerRoleRules(e, roles);
        if (!visible) return;

        if (e.name) {
          const reqDecisions = Array.isArray(e.required_for_decisions) ? e.required_for_decisions : [];
          const reqRoles = Array.isArray(e.required_for_roles) ? e.required_for_roles : [];
          const isReq =
            !!e.required ||
            (decisionStr && reqDecisions.map(s => String(s).trim()).includes(decisionStr)) ||
            _arrIntersects(roles, reqRoles);
          if (isReq && isEmpty(values?.[e.name])) {
            errors[e.name] = `${e.label || e.name} is required for this action`;
          }
        }

        if (e.elements) visit(e.elements);
        if (e.columns) e.columns.forEach(c => visit(c.elements));
        if (e.tabs) e.tabs.forEach(t => visit(t.elements));
      }
    });
  };
  visit(definition?.elements || definition || []);
  return errors;
}

// ─── Repeating-group helpers ──────────────────────────────────────────────────

// Recursively apply a row suffix (_i) to the `name` of every named field in an
// element tree, descending into columns/tabs/generic element containers.
// Layout containers (columns, tabs, sections, collapsibles, pages) have no `name`
// and are returned with their children updated in-place.
function suffixElementNames(elements, suffix) {
  return (elements || []).map(elem => {
    if (elem.name) return { ...elem, name: `${elem.name}${suffix}` };
    if (elem.type === 'columns' && elem.columns) {
      return { ...elem, columns: elem.columns.map(col => ({ ...col, elements: suffixElementNames(col.elements, suffix) })) };
    }
    if (elem.type === 'tabs' && elem.tabs) {
      return { ...elem, tabs: elem.tabs.map(tab => ({ ...tab, elements: suffixElementNames(tab.elements, suffix) })) };
    }
    if (elem.elements) return { ...elem, elements: suffixElementNames(elem.elements, suffix) };
    return elem;
  });
}

// Recursively collect { originalName: rowValue } for every named field inside a
// repeating-group row, regardless of nesting depth inside layout containers.
// All fields are stored with the `_i` suffix (via suffixElementNames), so we read
// row[`${name}_${rowIdx}`] at any depth.
function collectRowCtx(elements, row, rowIdx) {
  const ctx = {};
  const walk = (elems) => {
    (elems || []).forEach(c => {
      if (c.name) ctx[c.name] = row[`${c.name}_${rowIdx}`];
      if (c.elements && c.type !== 'repeating_group') walk(c.elements);
      if (c.columns) c.columns.forEach(col => walk(col.elements));
      if (c.tabs) c.tabs.forEach(tab => walk(tab.elements));
    });
  };
  walk(elements);
  return ctx;
}

// Strip the _i row-index suffix from every named field in repeating-group rows so
// the submitted payload uses clean field names (e.g. { startDate, endDate }) instead
// of the internal suffixed keys (e.g. { startDate_0, endDate_0 }).
// Only called at submit time — internal state always keeps the suffix.
function cleanSubmitValues(elements, values) {
  const result = { ...values };
  const walk = (elems) => {
    (elems || []).forEach(elem => {
      if (elem.type === 'repeating_group') {
        const rows = Array.isArray(values[elem.id]) ? values[elem.id] : [];
        result[elem.id] = rows.map((row, i) => {
          const clean = {};
          const collectClean = (children) => {
            (children || []).forEach(c => {
              if (c.name) {
                const suffixedKey = `${c.name}_${i}`;
                if (suffixedKey in row) clean[c.name] = row[suffixedKey];
              }
              if (c.elements && c.type !== 'repeating_group') collectClean(c.elements);
              if (c.columns) c.columns.forEach(col => collectClean(col.elements));
              if (c.tabs) c.tabs.forEach(tab => collectClean(tab.elements));
            });
          };
          collectClean(elem.elements);
          return clean;
        });
        // Also recurse into nested repeating groups within this group's elements
        walk(elem.elements || []);
      } else {
        if (elem.elements) walk(elem.elements);
        if (elem.columns) elem.columns.forEach(col => walk(col.elements));
        if (elem.tabs) elem.tabs.forEach(tab => walk(tab.elements));
      }
    });
  };
  walk(elements);
  return result;
}

// ─── Field renderer ───────────────────────────────────────────────────────────

function FieldInput({ elem, value, onChange, error, readOnly: forceReadOnly, allValues }) {
  const isRO = forceReadOnly || elem.type === 'readonly';
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: `calc(var(--xf-radius) - 2px)`,
    border: error ? '1.5px solid var(--xf-error)' : '1.5px solid var(--xf-border)',
    background: 'var(--xf-input-bg)', color: 'var(--xf-text)', fontSize: 14, outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s',
  };
  const focusStyle = { borderColor: 'var(--xf-primary)' };

  const [focused, setFocused] = useState(false);
  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);
  const merged = { ...inputStyle, ...(focused ? focusStyle : {}) };

  if (isRO) {
    const displayVal = elem.valueMode === 'derived'
      ? interpolate(elem.derivedExpression || '', allValues)
      : (elem.staticValue || value || '');
    const styles = {
      text: { padding: '10px 14px', borderRadius: `calc(var(--xf-radius) - 2px)`, color: 'var(--xf-muted)', fontSize: 14 },
      badge: { display: 'inline-block', padding: '4px 12px', borderRadius: 20, background: 'rgba(99,102,241,0.15)', color: 'var(--xf-primary)', fontSize: 13, fontWeight: 600 },
      code: { padding: '8px 12px', borderRadius: `calc(var(--xf-radius) - 2px)`, background: 'rgba(0,0,0,0.3)', color: '#a5f3fc', fontFamily: 'monospace', fontSize: 13, border: '1px solid var(--xf-border)' },
      currency: { padding: '10px 14px', fontSize: 16, fontWeight: 700, color: 'var(--xf-primary)' },
    };
    return <div style={styles[elem.displayAs] || styles.text}>{displayVal || '—'}</div>;
  }

  switch (elem.type) {
    case 'text': case 'email': case 'phone': case 'url': case 'password': {
      const prefix = elem.prefixText;
      const suffix = elem.suffixText;
      const inner = <input type={elem.type === 'password' ? 'password' : elem.type === 'email' ? 'email' : elem.type === 'url' ? 'url' : 'text'} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || ''} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
      if (!prefix && !suffix) return inner;
      return (
        <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${error ? 'var(--xf-error)' : focused ? 'var(--xf-primary)' : 'var(--xf-border)'}`, borderRadius: `calc(var(--xf-radius) - 2px)`, background: 'var(--xf-input-bg)', overflow: 'hidden' }}>
          {prefix && <span style={{ padding: '10px 10px', fontSize: 13, color: 'var(--xf-muted)', borderRight: '1px solid var(--xf-border)', whiteSpace: 'nowrap' }}>{prefix}</span>}
          <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || ''} style={{ flex: 1, padding: '10px 12px', border: 'none', background: 'transparent', color: 'var(--xf-text)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }} onFocus={handleFocus} onBlur={handleBlur} />
          {suffix && <span style={{ padding: '10px 10px', fontSize: 13, color: 'var(--xf-muted)', borderLeft: '1px solid var(--xf-border)', whiteSpace: 'nowrap' }}>{suffix}</span>}
        </div>
      );
    }
    case 'integer':
      return <input type="number" step="1" value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || '0'} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
    case 'float': case 'decimal': case 'currency': case 'percentage': case 'number':
      return <input type="number" step={elem.type === 'integer' ? '1' : 'any'} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || '0'} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
    case 'textarea':
      return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || ''} rows={elem.rows || 4} style={{ ...merged, resize: 'vertical' }} onFocus={handleFocus} onBlur={handleBlur} />;
    case 'rich_text': {
      const ref = useRef(null);
      const exec = (cmd) => { document.execCommand(cmd, false, null); if (ref.current) onChange(ref.current.innerHTML); };
      return (
        <div style={{ border: `1.5px solid ${error ? 'var(--xf-error)' : focused ? 'var(--xf-primary)' : 'var(--xf-border)'}`, borderRadius: `calc(var(--xf-radius) - 2px)`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 4, padding: '4px 8px', borderBottom: '1px solid var(--xf-border)', background: 'var(--xf-card)' }}>
            {[['B', 'bold'], ['I', 'italic'], ['U', 'underline'], ['•', 'insertUnorderedList'], ['1.', 'insertOrderedList']].map(([label, cmd]) => (
              <button key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }} style={{ padding: '2px 7px', borderRadius: 3, border: 'none', background: 'none', color: 'var(--xf-muted)', cursor: 'pointer', fontSize: 12, fontWeight: label === 'B' ? 700 : 400, fontStyle: label === 'I' ? 'italic' : 'normal' }}>{label}</button>
            ))}
          </div>
          <div ref={ref} contentEditable suppressContentEditableWarning onInput={() => onChange(ref.current?.innerHTML || '')} onFocus={handleFocus} onBlur={handleBlur} style={{ minHeight: 100, padding: '10px 14px', outline: 'none', color: 'var(--xf-text)', fontSize: 14, fontFamily: 'inherit' }} dangerouslySetInnerHTML={value ? undefined : { __html: '' }} />
        </div>
      );
    }
    case 'select': {
      const opts = elem.options || [];
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={merged} onFocus={handleFocus} onBlur={handleBlur}>
          <option value="">{elem.placeholder || 'Select…'}</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    case 'multiselect': {
      const opts = elem.options || [];
      const selected = Array.isArray(value) ? value : [];
      const toggle = (v) => selected.includes(v) ? onChange(selected.filter(x => x !== v)) : onChange([...selected, v]);
      return (
        <div style={{ border: `1.5px solid ${error ? 'var(--xf-error)' : 'var(--xf-border)'}`, borderRadius: `calc(var(--xf-radius) - 2px)`, padding: '8px 10px', display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 44, cursor: 'text', background: 'var(--xf-input-bg)' }}>
          {selected.map(v => <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: 'rgba(99,102,241,0.2)', color: 'var(--xf-primary)', fontSize: 13 }}>{(opts.find(o => o.value === v) || {}).label || v}<span onClick={() => toggle(v)} style={{ cursor: 'pointer', marginLeft: 2, opacity: 0.7 }}>×</span></span>)}
          <select onChange={e => { if (e.target.value) { toggle(e.target.value); e.target.value = ''; } }} style={{ border: 'none', background: 'transparent', color: 'var(--xf-muted)', fontSize: 13, outline: 'none', cursor: 'pointer', padding: '2px 4px' }}>
            <option value="">{selected.length ? '+ Add…' : elem.placeholder || 'Select…'}</option>
            {opts.filter(o => !selected.includes(o.value)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      );
    }
    case 'searchable_select': case 'combobox': {
      const [search, setSearch] = useState(value || '');
      const [open, setOpen] = useState(false);
      const opts = (elem.options || []).filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
      return (
        <div style={{ position: 'relative' }}>
          <input value={search} onChange={e => { setSearch(e.target.value); setOpen(true); if (elem.type === 'combobox') onChange(e.target.value); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={elem.placeholder || 'Search…'} style={merged} />
          {open && opts.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--xf-card)', border: '1px solid var(--xf-border)', borderRadius: 6, zIndex: 10, maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
              {opts.map(o => <div key={o.value} onMouseDown={() => { onChange(o.value); setSearch(o.label); setOpen(false); }} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: 'var(--xf-text)' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>{o.label}</div>)}
            </div>
          )}
        </div>
      );
    }
    case 'radio': {
      const opts = elem.options || [];
      return (
        <div style={{ display: 'flex', flexDirection: elem.layout === 'horizontal' ? 'row' : 'column', gap: 8, flexWrap: 'wrap' }}>
          {opts.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: 'var(--xf-text)', padding: '8px 14px', borderRadius: `calc(var(--xf-radius) - 2px)`, border: `1.5px solid ${value === o.value ? 'var(--xf-primary)' : 'var(--xf-border)'}`, background: value === o.value ? 'rgba(99,102,241,0.08)' : 'var(--xf-input-bg)' }}>
              <input type="radio" checked={value === o.value} onChange={() => onChange(o.value)} style={{ accentColor: 'var(--xf-primary)' }} />{o.label}
            </label>
          ))}
        </div>
      );
    }
    case 'radio_cards': {
      const opts = elem.options || [];
      return (
        <div style={{ display: 'grid', gridTemplateColumns: elem.layout === 'grid' ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {opts.map(o => (
            <div key={o.value} onClick={() => onChange(o.value)} style={{ padding: '14px 16px', borderRadius: 'var(--xf-radius)', border: `2px solid ${value === o.value ? 'var(--xf-primary)' : 'var(--xf-border)'}`, background: value === o.value ? 'rgba(99,102,241,0.08)' : 'var(--xf-input-bg)', cursor: 'pointer', textAlign: 'center' }}>
              {o.icon && <div style={{ fontSize: 20, marginBottom: 4 }}>{o.icon}</div>}
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--xf-text)' }}>{o.label}</div>
              {o.description && <div style={{ fontSize: 12, color: 'var(--xf-muted)', marginTop: 2 }}>{o.description}</div>}
            </div>
          ))}
        </div>
      );
    }
    case 'image_choice': {
      const opts = elem.options || [];
      const selected = Array.isArray(value) ? value : (value ? [value] : []);
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {opts.map(o => {
            const isSelected = elem.multiple ? selected.includes(o.value) : value === o.value;
            return (
              <div key={o.value} onClick={() => { if (elem.multiple) { const s = isSelected ? selected.filter(v => v !== o.value) : [...selected, o.value]; onChange(s); } else onChange(o.value); }} style={{ borderRadius: 8, border: `2px solid ${isSelected ? 'var(--xf-primary)' : 'var(--xf-border)'}`, overflow: 'hidden', cursor: 'pointer', background: 'var(--xf-input-bg)' }}>
                {o.imageUrl ? <img src={o.imageUrl} alt={o.label} style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} /> : <div style={{ height: 80, background: 'var(--xf-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--xf-muted)' }}>🖼</div>}
                <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--xf-text)', textAlign: 'center' }}>{o.label}</div>
              </div>
            );
          })}
        </div>
      );
    }
    case 'checkbox':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: 'var(--xf-text)' }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--xf-primary)' }} />
          {elem.label}
        </label>
      );
    case 'checkbox_group': {
      const opts = elem.options || [];
      const selected = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: 'flex', flexDirection: elem.layout === 'horizontal' ? 'row' : 'column', gap: 8, flexWrap: 'wrap' }}>
          {opts.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: 'var(--xf-text)', padding: '8px 14px', borderRadius: `calc(var(--xf-radius) - 2px)`, border: `1.5px solid ${selected.includes(o.value) ? 'var(--xf-primary)' : 'var(--xf-border)'}`, background: selected.includes(o.value) ? 'rgba(99,102,241,0.08)' : 'var(--xf-input-bg)' }}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={e => { const s = e.target.checked ? [...selected, o.value] : selected.filter(v => v !== o.value); onChange(s); }} style={{ accentColor: 'var(--xf-primary)' }} />{o.label}
            </label>
          ))}
        </div>
      );
    }
    case 'toggle': {
      const on = value === elem.onValue || value === true || value === 'true';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => onChange(on ? (elem.offValue || 'false') : (elem.onValue || 'true'))}>
          <div style={{ width: 44, height: 24, borderRadius: 12, background: on ? 'var(--xf-primary)' : 'var(--xf-border)', position: 'relative', transition: 'background 0.2s' }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: on ? 23 : 3, transition: 'left 0.2s' }} />
          </div>
          <span style={{ fontSize: 14, color: 'var(--xf-text)' }}>{on ? (elem.onLabel || 'Yes') : (elem.offLabel || 'No')}</span>
        </div>
      );
    }
    case 'rating': {
      const max = elem.max || 5;
      const icon = elem.icon === 'heart' ? '♥' : elem.icon === 'thumb' ? '👍' : '★';
      return (
        <div style={{ display: 'flex', gap: 4 }}>
          {Array.from({ length: max }, (_, i) => (
            <span key={i} onClick={() => onChange(i + 1)} style={{ fontSize: 28, cursor: 'pointer', color: (value || 0) > i ? 'var(--xf-primary)' : 'var(--xf-border)', transition: 'color 0.1s' }}>{icon}</span>
          ))}
        </div>
      );
    }
    case 'slider': {
      return (
        <div>
          <input type="range" min={elem.min ?? 0} max={elem.max ?? 100} step={elem.step || 1} value={value ?? elem.min ?? 0} onChange={e => onChange(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--xf-primary)' }} />
          {elem.showValue && <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--xf-primary)', marginTop: 4 }}>{value ?? elem.min ?? 0}</div>}
        </div>
      );
    }
    case 'range_slider': {
      const v = value || { from: elem.min ?? 0, to: elem.max ?? 100 };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--xf-muted)' }}>From</label>
              <input type="range" min={elem.min ?? 0} max={elem.max ?? 100} step={elem.step || 1} value={v.from} onChange={e => onChange({ ...v, from: Number(e.target.value) })} style={{ width: '100%', accentColor: 'var(--xf-primary)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--xf-muted)' }}>To</label>
              <input type="range" min={elem.min ?? 0} max={elem.max ?? 100} step={elem.step || 1} value={v.to} onChange={e => onChange({ ...v, to: Number(e.target.value) })} style={{ width: '100%', accentColor: 'var(--xf-primary)' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--xf-primary)' }}>
            <span>{v.from}</span><span>{v.to}</span>
          </div>
        </div>
      );
    }
    case 'nps': {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {Array.from({ length: 11 }, (_, i) => (
              <button key={i} onClick={() => onChange(i)} type="button" style={{ width: 38, height: 38, borderRadius: 6, border: `1.5px solid ${value === i ? 'var(--xf-primary)' : 'var(--xf-border)'}`, background: value === i ? 'var(--xf-primary)' : 'var(--xf-input-bg)', color: value === i ? '#fff' : 'var(--xf-text)', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>{i}</button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--xf-muted)' }}>
            <span>{elem.lowLabel || 'Not likely'}</span><span>{elem.highLabel || 'Very likely'}</span>
          </div>
        </div>
      );
    }
    case 'opinion_scale': {
      const min = elem.min || 1;
      const max = elem.max || 5;
      const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {nums.map(n => <button key={n} onClick={() => onChange(n)} type="button" style={{ flex: 1, height: 38, borderRadius: 6, border: `1.5px solid ${value === n ? 'var(--xf-primary)' : 'var(--xf-border)'}`, background: value === n ? 'var(--xf-primary)' : 'var(--xf-input-bg)', color: value === n ? '#fff' : 'var(--xf-text)', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>{n}</button>)}
          </div>
          {(elem.minLabel || elem.maxLabel) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--xf-muted)' }}>
              <span>{elem.minLabel}</span><span>{elem.maxLabel}</span>
            </div>
          )}
        </div>
      );
    }
    case 'matrix': {
      const rows = elem.rows || [];
      const cols = elem.columns || [];
      const v = value || {};
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr><th style={{ padding: '8px 12px' }}></th>{cols.map(c => <th key={c.id} style={{ padding: '8px 12px', fontSize: 12, color: 'var(--xf-muted)', fontWeight: 500, textAlign: 'center' }}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid var(--xf-border)' }}>
                  <td style={{ padding: '10px 12px', fontSize: 14, color: 'var(--xf-text)' }}>{row.label}</td>
                  {cols.map(col => (
                    <td key={col.id} style={{ textAlign: 'center', padding: '10px 12px' }}>
                      <input type={elem.multiple ? 'checkbox' : 'radio'} name={`matrix_${elem.id}_${row.id}`} checked={elem.multiple ? (Array.isArray(v[row.id]) && v[row.id].includes(col.id)) : v[row.id] === col.id} onChange={() => {
                        if (elem.multiple) {
                          const cur = Array.isArray(v[row.id]) ? v[row.id] : [];
                          onChange({ ...v, [row.id]: cur.includes(col.id) ? cur.filter(x => x !== col.id) : [...cur, col.id] });
                        } else onChange({ ...v, [row.id]: col.id });
                      }} style={{ accentColor: 'var(--xf-primary)', width: 16, height: 16 }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'date': {
      const todayStr = new Date().toISOString().split('T')[0];
      const minAttr = elem.validation?.minDate?.value === 'today' ? todayStr : (elem.validation?.minDate?.value || '');
      const maxAttr = elem.validation?.maxDate?.value === 'today' ? todayStr : (elem.validation?.maxDate?.value || '');
      return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} min={minAttr} max={maxAttr} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
    }
    case 'time':
      return <input type="time" value={value || ''} onChange={e => onChange(e.target.value)} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
    case 'datetime':
      return <input type="datetime-local" value={value || ''} onChange={e => onChange(e.target.value)} style={merged} onFocus={handleFocus} onBlur={handleBlur} />;
    case 'date_range':
      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={value?.from || ''} onChange={e => onChange({ ...(value || {}), from: e.target.value })} style={{ ...merged, flex: 1 }} onFocus={handleFocus} onBlur={handleBlur} />
          <span style={{ color: 'var(--xf-muted)', fontSize: 13, flexShrink: 0 }}>→</span>
          <input type="date" value={value?.to || ''} onChange={e => onChange({ ...(value || {}), to: e.target.value })} style={{ ...merged, flex: 1 }} onFocus={handleFocus} onBlur={handleBlur} />
        </div>
      );
    case 'file': {
      return (
        <div>
          <label style={{ display: 'block', border: `2px dashed ${error ? 'var(--xf-error)' : 'var(--xf-border)'}`, borderRadius: 'var(--xf-radius)', padding: '20px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--xf-muted)', fontSize: 13 }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>📎</div>
            {elem.dropzoneLabel || 'Click or drag files here'}
            <input type="file" accept={elem.accept} multiple={elem.multiple} onChange={e => { const files = Array.from(e.target.files); Promise.all(files.map(f => new Promise(res => { const r = new FileReader(); r.onload = () => res({ name: f.name, type: f.type, size: f.size, content: r.result.split(',')[1] }); r.readAsDataURL(f); }))).then(onChange); }} style={{ display: 'none' }} />
          </label>
          {Array.isArray(value) && value.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {value.map((f, i) => <div key={i} style={{ fontSize: 12, color: 'var(--xf-primary)', display: 'flex', justifyContent: 'space-between' }}><span>✓ {f.name}</span><span style={{ color: 'var(--xf-muted)' }}>{(f.size / 1024).toFixed(1)} KB</span></div>)}
            </div>
          )}
        </div>
      );
    }
    case 'image': {
      return (
        <div>
          <label style={{ display: 'block', border: `2px dashed ${error ? 'var(--xf-error)' : 'var(--xf-border)'}`, borderRadius: 'var(--xf-radius)', padding: '20px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--xf-muted)', fontSize: 13 }}>
            {value?.content ? <img src={`data:${value.type};base64,${value.content}`} alt="" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} /> : <div style={{ fontSize: 24, marginBottom: 6 }}>🖼</div>}
            {value?.name || 'Click to upload image'}
            <input type="file" accept={elem.accept || 'image/*'} onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => onChange({ name: f.name, type: f.type, size: f.size, content: r.result.split(',')[1] }); r.readAsDataURL(f); }} style={{ display: 'none' }} />
          </label>
        </div>
      );
    }
    case 'signature': {
      const canvasRef = useRef(null);
      const drawing = useRef(false);
      const getPos = (e, canvas) => { const rect = canvas.getBoundingClientRect(); const src = e.touches?.[0] || e; return { x: src.clientX - rect.left, y: src.clientY - rect.top }; };
      const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const pos = getPos(e, canvasRef.current); ctx.beginPath(); ctx.moveTo(pos.x, pos.y); };
      const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const pos = getPos(e, canvasRef.current); ctx.lineWidth = elem.strokeWidth || 2; ctx.strokeStyle = elem.strokeColor || '#000'; ctx.lineTo(pos.x, pos.y); ctx.stroke(); };
      const end = () => { drawing.current = false; onChange(canvasRef.current.toDataURL('image/png').split(',')[1]); };
      const clear = () => { const ctx = canvasRef.current.getContext('2d'); ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); onChange(null); };
      return (
        <div>
          <canvas ref={canvasRef} width={500} height={120} onMouseDown={start} onMouseMove={move} onMouseUp={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} style={{ width: '100%', height: 120, borderRadius: `calc(var(--xf-radius) - 2px)`, border: `1.5px solid ${error ? 'var(--xf-error)' : 'var(--xf-border)'}`, background: elem.bgColor || '#fff', display: 'block', cursor: 'crosshair', touchAction: 'none' }} />
          <button type="button" onClick={clear} style={{ marginTop: 6, fontSize: 12, color: 'var(--xf-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
        </div>
      );
    }
    case 'address': {
      const fields = elem.fields || ['street', 'city', 'state', 'zip', 'country'];
      const v = value || {};
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {fields.map(f => (
            <div key={f} style={{ gridColumn: f === 'street' || f === 'street2' ? '1 / -1' : undefined }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--xf-muted)', marginBottom: 3, textTransform: 'capitalize' }}>{f.replace('_', ' ')}</label>
              <input value={v[f] || ''} onChange={e => onChange({ ...v, [f]: e.target.value })} placeholder={f.charAt(0).toUpperCase() + f.slice(1)} style={inputStyle} />
            </div>
          ))}
        </div>
      );
    }
    case 'name': {
      const fields = elem.fields || ['first', 'last'];
      const v = value || {};
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          {fields.map(f => (
            <div key={f}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--xf-muted)', marginBottom: 3, textTransform: 'capitalize' }}>{f}</label>
              <input value={v[f] || ''} onChange={e => onChange({ ...v, [f]: e.target.value })} placeholder={f.charAt(0).toUpperCase() + f.slice(1)} style={inputStyle} />
            </div>
          ))}
        </div>
      );
    }
    case 'color': {
      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="color" value={value || '#6366f1'} onChange={e => onChange(e.target.value)} style={{ width: 44, height: 40, borderRadius: 6, border: '1.5px solid var(--xf-border)', cursor: 'pointer', padding: 2 }} />
          <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#6366f1" style={{ ...merged, width: 120 }} />
          {(elem.presets || []).length > 0 && (
            <div style={{ display: 'flex', gap: 4 }}>
              {elem.presets.map(p => <div key={p} onClick={() => onChange(p)} style={{ width: 24, height: 24, borderRadius: '50%', background: p, cursor: 'pointer', border: '2px solid var(--xf-border)' }} />)}
            </div>
          )}
        </div>
      );
    }
    case 'map_picker': {
      return (
        <div style={{ border: `1.5px solid ${error ? 'var(--xf-error)' : 'var(--xf-border)'}`, borderRadius: 'var(--xf-radius)', overflow: 'hidden', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--xf-input-bg)', color: 'var(--xf-muted)', fontSize: 13, flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 28 }}>🗺</span>
          {value ? <span style={{ fontSize: 12, color: 'var(--xf-primary)' }}>📍 {value.lat?.toFixed(4)}, {value.lng?.toFixed(4)}</span> : 'Map picker (interactive in production)'}
        </div>
      );
    }
    case 'hidden': return null;
    case 'captcha': return null;
    default:
      return <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={elem.placeholder || ''} style={merged} />;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function interpolate(tpl, values) {
  if (!tpl) return '';
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => values[k] ?? '');
}

function evaluateConditionals(conditionals, values) {
  const result = { visible: true, required: false, readonly: false, label: null };
  if (!conditionals?.length) return result;

  const evalRules = (rules, operator) => {
    const evalRule = (r) => {
      const fv = String(values[r.field] ?? '');
      const rv = String(r.value ?? '');
      switch (r.op) {
        case 'eq': return fv === rv;
        case 'neq': return fv !== rv;
        case 'contains': return fv.includes(rv);
        case 'startsWith': return fv.startsWith(rv);
        case 'gt': return parseFloat(fv) > parseFloat(rv);
        case 'lt': return parseFloat(fv) < parseFloat(rv);
        case 'gte': return parseFloat(fv) >= parseFloat(rv);
        case 'lte': return parseFloat(fv) <= parseFloat(rv);
        case 'empty': return !fv || fv === 'undefined';
        case 'notEmpty': return !!fv && fv !== 'undefined';
        default: return false;
      }
    };
    return operator === 'or' ? rules.some(evalRule) : rules.every(evalRule);
  };

  for (const cond of conditionals) {
    if (!cond.rules?.length) continue;
    const matches = evalRules(cond.rules, cond.operator);
    if (cond.action === 'show' && !matches) result.visible = false;
    if (cond.action === 'hide' && matches) result.visible = false;
    if (cond.action === 'require' && matches) result.required = true;
    if (cond.action === 'readonly' && matches) result.readonly = true;
    if (cond.action === 'setLabel' && matches && cond.labelTemplate) result.label = interpolate(cond.labelTemplate, values);
  }
  return result;
}

function validateField(elem, value, allValues) {
  const v = elem.validation || {};
  const required = elem.required;

  // For checkbox: required means must be checked (true)
  const isCheckbox = elem.type === 'checkbox';
  const isEmpty = isCheckbox
    ? (value !== true && value !== 'true')
    : (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0));

  if (required && isEmpty) return v.required?.message || (isCheckbox ? 'This field must be checked' : 'This field is required');

  if (isEmpty) return null; // not required, skip further validation

  // String rules
  if (typeof value === 'string') {
    if (v.minLength?.value && value.length < Number(v.minLength.value)) return v.minLength.message || `At least ${v.minLength.value} characters`;
    if (v.maxLength?.value && value.length > Number(v.maxLength.value)) return v.maxLength.message || `No more than ${v.maxLength.value} characters`;
    if (v.notEmpty && !value.trim()) return v.notEmpty.message || 'Cannot be blank';
    if (v.pattern?.value) { try { if (!new RegExp(v.pattern.value).test(value)) return v.pattern.message || 'Invalid format'; } catch {} }

    // Auto-validate based on field type (regardless of whether explicit rule was added)
    if (elem.type === 'email' || v.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return v.email?.message || 'Enter a valid email address';
    }
    if (elem.type === 'url' || v.url) {
      try {
        const u = new URL(value);
        if ((elem.requireHttps || v.url?.requireHttps) && u.protocol !== 'https:') return v.url?.message || 'URL must use HTTPS';
      } catch {
        return v.url?.message || 'Enter a valid URL (include https://)';
      }
    }
    if (elem.type === 'phone' && !/^\+?[\d\s\-().]{7,20}$/.test(value)) return 'Enter a valid phone number';

    // Password strength
    if (elem.type === 'password' && elem.strengthRules?.minLength) {
      if (value.length < Number(elem.strengthRules.minLength)) return `Password must be at least ${elem.strengthRules.minLength} characters`;
    }
  }

  // Numeric rules
  const num = parseFloat(value);
  if (!isNaN(num)) {
    // Support min/max stored directly on elem (from properties panel) as well as inside validation
    const minVal = v.min?.value !== undefined ? v.min.value : elem.min;
    const maxVal = v.max?.value !== undefined ? v.max.value : elem.max;
    if (minVal !== undefined && minVal !== '' && num < Number(minVal)) return v.min?.message || `Must be at least ${minVal}`;
    if (maxVal !== undefined && maxVal !== '' && num > Number(maxVal)) return v.max?.message || `Must be at most ${maxVal}`;
    if (v.minExclusive?.value !== undefined && num <= Number(v.minExclusive.value)) return v.minExclusive.message || `Must be greater than ${v.minExclusive.value}`;
    if (v.maxExclusive?.value !== undefined && num >= Number(v.maxExclusive.value)) return v.maxExclusive.message || `Must be less than ${v.maxExclusive.value}`;
    if (v.positive && num < 0) return v.positive.message || 'Must be positive';
    if (v.negative && num > 0) return v.negative.message || 'Must be negative';
    if (v.notZero && num === 0) return v.notZero.message || 'Cannot be zero';
    if ((elem.type === 'integer' || v.integer) && !Number.isInteger(num)) return v.integer?.message || 'Must be a whole number';
    if (v.decimalPlaces?.value !== undefined) { const dp = String(value).split('.')[1]; if (dp && dp.length > Number(v.decimalPlaces.value)) return v.decimalPlaces.message || `Max ${v.decimalPlaces.value} decimal places`; }
  }

  // Date rules
  if (['date', 'datetime', 'datetime-local'].includes(elem.type) && typeof value === 'string' && value) {
    const dateVal = new Date(value);
    if (v.minDate?.value) {
      const minD = v.minDate.value === 'today' ? new Date() : new Date(v.minDate.value);
      if (dateVal < minD) return v.minDate.message || `Date must be on or after ${v.minDate.value === 'today' ? 'today' : v.minDate.value}`;
    }
    if (v.maxDate?.value) {
      const maxD = v.maxDate.value === 'today' ? new Date() : new Date(v.maxDate.value);
      if (dateVal > maxD) return v.maxDate.message || `Date must be on or before ${v.maxDate.value === 'today' ? 'today' : v.maxDate.value}`;
    }
  }

  // File rules
  if (['file', 'image'].includes(elem.type)) {
    const files = Array.isArray(value) ? value : (value ? [value] : []);
    if (v.maxFiles?.value && files.length > Number(v.maxFiles.value)) return v.maxFiles.message || `Upload at most ${v.maxFiles.value} file(s)`;
    if (v.maxSizeMb?.value) {
      const maxBytes = Number(v.maxSizeMb.value) * 1024 * 1024;
      const oversized = files.find(f => f.size > maxBytes);
      if (oversized) return v.maxSizeMb.message || `File "${oversized.name}" exceeds ${v.maxSizeMb.value} MB limit`;
    }
  }

  // Choice rules
  if (Array.isArray(value)) {
    if (v.minSelections?.value && value.length < Number(v.minSelections.value)) return v.minSelections.message || `Select at least ${v.minSelections.value}`;
    if (v.maxSelections?.value && value.length > Number(v.maxSelections.value)) return v.maxSelections.message || `Select at most ${v.maxSelections.value}`;
  }

  // Cross-field
  if (v.mustMatch?.field && value !== allValues[v.mustMatch.field]) return v.mustMatch.message || 'Values must match';
  if (v.greaterThan?.field) {
    const otherGT = allValues[v.greaterThan.field];
    const isDateCmpGT = ['date', 'datetime', 'datetime-local'].includes(elem.type) && value && otherGT;
    const gtFails = isDateCmpGT ? new Date(value) <= new Date(otherGT) : value <= otherGT;
    if (gtFails) return v.greaterThan.message || `Must be after ${v.greaterThan.field}`;
  }
  if (v.lessThan?.field) {
    const otherLT = allValues[v.lessThan.field];
    const isDateCmpLT = ['date', 'datetime', 'datetime-local'].includes(elem.type) && value && otherLT;
    const ltFails = isDateCmpLT ? new Date(value) >= new Date(otherLT) : value >= otherLT;
    if (ltFails) return v.lessThan.message || `Must be before ${v.lessThan.field}`;
  }

  return null;
}

// ─── Element renderer ─────────────────────────────────────────────────────────

function RenderElement({ elem, values, onChange, errors, allValues, displayOnly }) {
  const { actorRoles } = React.useContext(RuleContext);
  const { visible, required: condRequired, readonly: condReadonly, label: condLabel } = evaluateConditionals(elem.conditionals, allValues);
  const cm = evaluateCaseManagerRoleRules(elem, actorRoles);
  if (!visible || !cm.visible) return null;

  const effectiveRequired = elem.required || condRequired;
  const effectiveReadonly = condReadonly || cm.readonly;
  const effectiveLabel = condLabel || elem.label;

  // Derived value for hidden/readonly
  let derivedValue = values[elem.name];
  if (elem.valueMode === 'derived' && (elem.type === 'hidden' || elem.type === 'readonly')) {
    derivedValue = interpolate(elem.derivedExpression || '', allValues);
    if (derivedValue !== values[elem.name]) onChange(elem.name, derivedValue);
  }

  const isLayout = ['columns', 'tabs', 'collapsible', 'section', 'repeating_group', 'page'].includes(elem.type);
  const isDisplay = ['html', 'image_display', 'video_embed', 'progress_display', 'divider', 'spacer'].includes(elem.type);

  if (isDisplay) {
    switch (elem.type) {
      case 'html': return <div style={{ margin: '12px 0' }} dangerouslySetInnerHTML={{ __html: elem.content || '' }} />;
      case 'image_display': return elem.src ? <div style={{ textAlign: elem.alignment || 'center', margin: '12px 0' }}><img src={elem.src} alt={elem.alt || ''} style={{ maxWidth: elem.width || '100%', borderRadius: 6 }} /></div> : null;
      case 'video_embed': {
        if (!elem.url) return null;
        const url = elem.url.includes('youtu') ? elem.url.replace('watch?v=', 'embed/') : elem.url.replace('vimeo.com/', 'player.vimeo.com/video/');
        return <div style={{ margin: '12px 0' }}><iframe src={url} title={elem.title || 'video'} style={{ width: '100%', height: 240, borderRadius: 8, border: 'none' }} allowFullScreen /></div>;
      }
      case 'progress_display': {
        const allNamedFields = [];
        const collect = (els) => { (els || []).forEach(e => { if (e.name && !['hidden', 'readonly', 'captcha'].includes(e.type)) allNamedFields.push(e); if (e.elements) collect(e.elements); if (e.columns) e.columns.forEach(c => collect(c.elements)); if (e.tabs) e.tabs.forEach(t => collect(t.elements)); }); };
        const reqFields = allNamedFields.filter(f => f.required);
        const filled = reqFields.filter(f => { const v = allValues[f.name]; return v !== undefined && v !== null && v !== '' && (!Array.isArray(v) || v.length > 0); });
        const pct = reqFields.length > 0 ? Math.round((filled.length / reqFields.length) * 100) : 0;
        return <div style={{ margin: '12px 0' }}><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--xf-muted)', marginBottom: 4 }}><span>Progress</span><span>{pct}%</span></div><div style={{ height: 6, background: 'var(--xf-border)', borderRadius: 3 }}><div style={{ width: `${pct}%`, height: '100%', background: 'var(--xf-primary)', borderRadius: 3, transition: 'width 0.3s' }} /></div></div>;
      }
      case 'divider': return <div style={{ margin: '16px 0', borderTop: '1px solid var(--xf-border)', position: 'relative', textAlign: 'center' }}>{elem.label && <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'var(--xf-card)', padding: '0 12px', fontSize: 12, color: 'var(--xf-muted)' }}>{elem.label}</span>}</div>;
      case 'spacer': return <div style={{ height: elem.height || 24 }} />;
    }
  }

  if (elem.type === 'columns') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: (elem.columns || []).map(c => `${c.span || 6}fr`).join(' '), gap: elem.gap || 16, margin: '8px 0' }}>
        {(elem.columns || []).map(col => (
          <div key={col.id}>
            {(col.elements || []).map(child => <RenderElement key={child.id} elem={child} values={values} onChange={onChange} errors={errors} allValues={allValues} displayOnly={displayOnly} />)}
          </div>
        ))}
      </div>
    );
  }

  if (elem.type === 'tabs') {
    const [activeTab, setActiveTab] = useState(0);
    return (
      <div style={{ margin: '12px 0' }}>
        <div style={{ display: 'flex', borderBottom: '2px solid var(--xf-border)', marginBottom: 16, gap: 4 }}>
          {(elem.tabs || []).map((tab, i) => (
            <button key={tab.id} onClick={() => setActiveTab(i)} type="button" style={{ padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === i ? 600 : 400, color: activeTab === i ? 'var(--xf-primary)' : 'var(--xf-muted)', borderBottom: activeTab === i ? '2px solid var(--xf-primary)' : '2px solid transparent', marginBottom: -2 }}>
              {tab.icon && <span style={{ marginRight: 6 }}>{tab.icon}</span>}{tab.label}
            </button>
          ))}
        </div>
        <div>
          {(elem.tabs?.[activeTab]?.elements || []).map(child => <RenderElement key={child.id} elem={child} values={values} onChange={onChange} errors={errors} allValues={allValues} displayOnly={displayOnly} />)}
        </div>
      </div>
    );
  }

  if (elem.type === 'collapsible') {
    const [open, setOpen] = useState(!!elem.defaultOpen);
    return (
      <div style={{ border: '1px solid var(--xf-border)', borderRadius: 'var(--xf-radius)', margin: '8px 0', overflow: 'hidden' }}>
        <div onClick={() => setOpen(o => !o)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.1)' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--xf-text)' }}>{elem.label}</span>
          <span style={{ color: 'var(--xf-muted)', transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
        </div>
        {open && <div style={{ padding: '12px 16px' }}>{(elem.elements || []).map(child => <RenderElement key={child.id} elem={child} values={values} onChange={onChange} errors={errors} allValues={allValues} displayOnly={displayOnly} />)}</div>}
      </div>
    );
  }

  if (elem.type === 'section') {
    return (
      <div style={{ margin: '16px 0' }}>
        {elem.label && <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--xf-text)', marginBottom: elem.description ? 4 : 12 }}>{elem.label}</div>}
        {elem.description && <div style={{ fontSize: 13, color: 'var(--xf-muted)', marginBottom: 12 }}>{elem.description}</div>}
        {(elem.elements || []).map(child => <RenderElement key={child.id} elem={child} values={values} onChange={onChange} errors={errors} allValues={allValues} displayOnly={displayOnly} />)}
      </div>
    );
  }

  if (elem.type === 'page') {
    return (
      <div style={{ margin: '16px 0', border: '1px dashed var(--xf-border)', borderRadius: 'var(--xf-radius)', padding: '16px 20px' }}>
        {elem.label && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--xf-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Step: {elem.label}</div>}
        {(elem.elements || []).map(child => <RenderElement key={child.id} elem={child} values={values} onChange={onChange} errors={errors} allValues={allValues} displayOnly={displayOnly} />)}
      </div>
    );
  }

  if (elem.type === 'repeating_group') {
    const rows = Array.isArray(values[elem.id]) ? values[elem.id] : [{}];
    const updateRow = (i, rowValues) => { const next = [...rows]; next[i] = rowValues; onChange(elem.id, next); };

    // Recursively init boolean fields in a new row at any nesting depth.
    const initRowBooleans = (elems, rowIdx) => {
      const row = {};
      const walk = (es) => (es || []).forEach(c => {
        if (c.name) {
          if (c.type === 'checkbox') row[`${c.name}_${rowIdx}`] = false;
          else if (c.type === 'toggle') row[`${c.name}_${rowIdx}`] = c.offValue || 'false';
        }
        if (c.elements && c.type !== 'repeating_group') walk(c.elements);
        if (c.columns) c.columns.forEach(col => walk(col.elements));
        if (c.tabs) c.tabs.forEach(tab => walk(tab.elements));
      });
      walk(elems);
      return row;
    };

    const addRow = () => {
      if (rows.length < (elem.maxRows || 20)) {
        onChange(elem.id, [...rows, initRowBooleans(elem.elements, rows.length)]);
      }
    };
    const removeRow = (i) => { if (rows.length > (elem.minRows || 1)) onChange(elem.id, rows.filter((_, j) => j !== i)); };
    return (
      <div style={{ margin: '12px 0' }}>
        {elem.label && <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--xf-text)', marginBottom: 8 }}>{elem.label}</div>}
        {rows.map((row, i) => {
          // Build a per-row context: original field names → suffixed stored values, recursively
          // through any nested layout containers, so within-row conditionals and cross-field
          // validation can reference sibling fields by original name (e.g. "startDate").
          const rowCtx = collectRowCtx(elem.elements, row, i);
          const rowAllValues = { ...allValues, ...rowCtx };
          // Apply _i suffix to ALL named fields at any depth so the renderer, value
          // storage, error keys, and buildErrors all use the same key convention.
          const suffixedChildren = suffixElementNames(elem.elements, `_${i}`);
          return (
          <div key={i} style={{ border: '1px solid var(--xf-border)', borderRadius: 'var(--xf-radius)', padding: '12px 16px', marginBottom: 10, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--xf-muted)' }}>{elem.rowLabel || 'Row'} {i + 1}</span>
              {rows.length > (elem.minRows || 1) && <button type="button" onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', color: 'var(--xf-error)', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>}
            </div>
            {suffixedChildren.map(child => <RenderElement key={child.id} elem={child} values={row} onChange={(k, v) => updateRow(i, { ...row, [k]: v })} errors={errors} allValues={rowAllValues} displayOnly={displayOnly} />)}
          </div>
          );
        })}
        {rows.length < (elem.maxRows || 20) && <button type="button" onClick={addRow} style={{ color: 'var(--xf-primary)', background: 'none', border: `1px solid var(--xf-primary)`, borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>{elem.addLabel || '+ Add row'}</button>}
      </div>
    );
  }

  if (elem.type === 'hidden') return null;
  if (elem.type === 'captcha') return null;

  const err = errors[elem.name];
  const widthMap = { full: '100%', half: 'calc(50% - 8px)', third: 'calc(33.333% - 8px)', quarter: 'calc(25% - 8px)' };

  // Display-only mode: show label + static value instead of input fields
  if (displayOnly) {
    const v = values[elem.name];
    let displayValue;
    if (v === undefined || v === null || v === '') {
      displayValue = '—';
    } else if (elem.type === 'checkbox') {
      displayValue = v ? 'Yes' : 'No';
    } else if (elem.type === 'toggle') {
      displayValue = v === (elem.onValue || 'true') ? (elem.onLabel || 'On') : (elem.offLabel || 'Off');
    } else if (elem.type === 'select' || elem.type === 'radio') {
      const opt = (elem.options || []).find(o => (typeof o === 'object' ? o.value : o) === v);
      displayValue = opt ? (typeof opt === 'object' ? opt.label : opt) : String(v);
    } else if (elem.type === 'multi_select' || (Array.isArray(v) && typeof v[0] === 'string')) {
      displayValue = Array.isArray(v) ? v.join(', ') : String(v);
    } else if (elem.type === 'file' || elem.type === 'image') {
      displayValue = Array.isArray(v) ? v.map(f => f.name || 'file').join(', ') : (v?.name || 'file');
    } else if (elem.type === 'rating') {
      displayValue = '★'.repeat(Number(v) || 0) + '☆'.repeat((elem.max || 5) - (Number(v) || 0));
    } else if (elem.type === 'color') {
      displayValue = v;
    } else {
      displayValue = String(v);
    }
    return (
      <div data-field={elem.name} style={{ marginBottom: 12, width: widthMap[elem.width] || '100%', display: 'inline-block', verticalAlign: 'top', boxSizing: 'border-box', paddingRight: ['half', 'third', 'quarter'].includes(elem.width) ? 8 : 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary, var(--xf-muted))', marginBottom: 3 }}>{effectiveLabel}</div>
        <div style={{ fontSize: 13, color: v === undefined || v === null || v === '' ? 'var(--text-tertiary, var(--xf-muted))' : 'var(--text-primary, var(--xf-text))', lineHeight: 1.5 }}>
          {elem.type === 'color' && v ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: v, display: 'inline-block', border: '1px solid var(--xf-border)' }} />
              {v}
            </span>
          ) : displayValue}
        </div>
      </div>
    );
  }

  return (
    <div data-field={elem.name} style={{ marginBottom: 18, width: widthMap[elem.width] || '100%', display: 'inline-block', verticalAlign: 'top', boxSizing: 'border-box', paddingRight: ['half', 'third', 'quarter'].includes(elem.width) ? 8 : 0 }}>
      {!['checkbox'].includes(elem.type) && (
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--xf-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          {effectiveLabel}
          {effectiveRequired && <span style={{ color: 'var(--xf-primary)', fontSize: 11 }}>*</span>}
          {elem.tooltip && <span title={elem.tooltip} style={{ cursor: 'help', color: 'var(--xf-muted)', border: '1px solid var(--xf-border)', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>?</span>}
        </label>
      )}
      <FieldInput elem={{ ...elem, required: effectiveRequired }} value={values[elem.name]} onChange={v => onChange(elem.name, v)} error={err} readOnly={effectiveReadonly} allValues={allValues} />
      {elem.helpText && !err && <div style={{ fontSize: 12, color: 'var(--xf-muted)', marginTop: 4 }}>{elem.helpText}</div>}
      {err && <div style={{ fontSize: 12, color: 'var(--xf-error)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ─── Main Renderer ────────────────────────────────────────────────────────────

// Parse interaction params injected by the Form Input node email link redirect.
// These are stripped before the form field values are submitted.
const _XF_INT_PARAM = '_xf_int';
const _XF_ITOK_PARAM = '_xf_itok';

function _getInteractionParams() {
  const p = new URLSearchParams(window.location.search);
  const intId = p.get(_XF_INT_PARAM) || '';
  const itok = p.get(_XF_ITOK_PARAM) || '';
  return intId && itok ? { intId, itok } : null;
}

export default function XavvyFormRenderer({ token, definition, initialValues, readOnly, onValuesChange, actorRoles }) {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(definition ? false : true);
  const [error, setError] = useState(null);
  const [values, setValues] = useState(initialValues || {});
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pwProtected, setPwProtected] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState('');

  // Theme mode.
  // - Embedded (definition/onValuesChange passed in, e.g. CaseManager intake
  //   modal): follow the host app's [data-theme] on <html>, live-updating if
  //   the user toggles the app theme. This is the only theming path we still
  //   support while we rethink the overall model.
  // - Standalone (public form via token): no theming logic — always renders
  //   in the fixed slate-dark look, respecting whatever colors the creator
  //   stored on the form.
  const _isEmbeddedRenderer = !!(definition || onValuesChange);
  const [themeMode, setThemeMode] = useState(() => {
    if (_isEmbeddedRenderer && typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    }
    return 'dark';
  });
  // Embedded: watch the <html data-theme> attribute for live app-theme changes.
  useEffect(() => {
    if (!_isEmbeddedRenderer || typeof document === 'undefined') return;
    const root = document.documentElement;
    const read = () => root.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    setThemeMode(read());
    const mo = new MutationObserver(() => setThemeMode(read()));
    mo.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, [_isEmbeddedRenderer]);

  // Interaction mode: set once on mount, never changes
  const interactionParams = useState(() => _getInteractionParams())[0];

  // When definition is passed directly (e.g. case detail read-only view), skip fetch
  useEffect(() => {
    if (definition) {
      setFormData({ open: true, definition });
      if (initialValues) setValues(v => ({ ...v, ...initialValues }));
      setLoading(false);
      return;
    }
    if (!token) { setError('Form not found'); setLoading(false); return; }
    fetch(`/xf/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status === 404 ? 'Form not found' : 'Form is not available'))
      .then(d => {
        setFormData(d);
        // Initialise boolean fields so conditions based on "eq false" fire immediately
        // without requiring the user to interact with the field first.
        const initVals = {};
        // Build the default boolean-field values for a single repeating-group row.
        const makeBoolRowDefaults = (children, rowIdx) => {
          const row = {};
          const wr = (elems) => (elems || []).forEach(c => {
            if (c.name) {
              if (c.type === 'checkbox') row[`${c.name}_${rowIdx}`] = false;
              else if (c.type === 'toggle') row[`${c.name}_${rowIdx}`] = c.offValue || 'false';
            }
            if (c.elements && c.type !== 'repeating_group') wr(c.elements);
            if (c.columns) c.columns.forEach(col => wr(col.elements));
            if (c.tabs) c.tabs.forEach(tab => wr(tab.elements));
          });
          wr(children);
          return row;
        };
        const walkInit = (elems) => {
          (elems || []).forEach(e => {
            if (e.type === 'repeating_group') {
              // Initialise the first (default) row with boolean defaults.
              const rowDefs = makeBoolRowDefaults(e.elements || [], 0);
              if (Object.keys(rowDefs).length) initVals[e.id] = [rowDefs];
              return; // children are owned by the group — don't set top-level keys
            }
            if (e.name) {
              if (e.type === 'checkbox') initVals[e.name] = false;
              else if (e.type === 'toggle') initVals[e.name] = e.offValue || 'false';
            }
            if (e.elements) walkInit(e.elements);
            if (e.columns) e.columns.forEach(c => walkInit(c.elements));
            if (e.tabs) e.tabs.forEach(t => walkInit(t.elements));
          });
        };
        walkInit(d.definition?.elements || []);
        if (Object.keys(initVals).length) setValues(v => ({ ...initVals, ...v }));
        const settings = d.definition?.settings || {};
        // Always apply URL params as prefill when in interaction mode;
        // otherwise respect the form's prefillFromUrl setting.
        if (interactionParams || settings.prefillFromUrl) {
          const params = new URLSearchParams(window.location.search);
          const prefill = {};
          params.forEach((v, k) => {
            if (k !== _XF_INT_PARAM && k !== _XF_ITOK_PARAM) prefill[k] = v;
          });
          if (Object.keys(prefill).length) setValues(v => ({ ...v, ...prefill }));
        }
        // Restore auto-save (skip in interaction mode to avoid stale data overwriting prefill)
        if (settings.autoSave && !interactionParams) {
          const saved = localStorage.getItem(`xf_${token}`);
          if (saved) { try { setValues(JSON.parse(saved)); } catch {} }
        }
        // Check password
        if (settings.passwordProtected) setPwProtected(true);
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [token, definition]);

  const handleChange = useCallback((name, value) => {
    if (readOnly) return;
    setValues(v => {
      const next = { ...v, [name]: value };
      // Auto-save
      if (formData?.definition?.settings?.autoSave && !onValuesChange) {
        try { localStorage.setItem(`xf_${token}`, JSON.stringify(next)); } catch {}
      }
      if (onValuesChange) onValuesChange(next);
      return next;
    });
    if (errors[name]) setErrors(e => { const n = { ...e }; delete n[name]; return n; });
  }, [formData, errors, token, readOnly, onValuesChange]);

  // Recursively validate elements, fully handling repeating groups.
  // - ctx: the field-value map for the current scope (top-level or per-row merged)
  // - suffix: "_i" when inside a repeating group row (used for error keys)
  const buildErrors = (elems, ctx, suffix = '') => {
    const errs = {};
    const walk = (es, ctx, suffix) => {
      (es || []).forEach(elem => {
        if (elem.type === 'repeating_group') {
          const rows = Array.isArray(values[elem.id]) ? values[elem.id] : [{}];
          rows.forEach((row, i) => {
            // collectRowCtx recurses into columns/tabs/sections so cross-field validation
            // works regardless of nesting depth inside the repeating group.
            const rowCtx = collectRowCtx(elem.elements, row, i);
            walk(elem.elements, { ...ctx, ...rowCtx }, `_${i}`);
          });
          return;
        }
        // Layout containers — recurse without changing scope
        if (!elem.name) {
          walk(elem.elements, ctx, suffix);
          if (elem.columns) elem.columns.forEach(c => walk(c.elements, ctx, suffix));
          if (elem.tabs) elem.tabs.forEach(t => walk(t.elements, ctx, suffix));
          return;
        }
        const { visible, required: condRequired } = evaluateConditionals(elem.conditionals, ctx);
        if (!visible) return;
        const effectiveRequired = elem.required || condRequired;
        const err = validateField({ ...elem, required: effectiveRequired }, ctx[elem.name], ctx);
        if (err) errs[`${elem.name}${suffix}`] = err;
      });
    };
    walk(elems, ctx, suffix);
    return errs;
  };

  const validateStep = (stepElems) => buildErrors(stepElems, values);

  const def = formData?.definition || {};
  const settings = def.settings || {};
  const theme = def.theme || {};
  const elements = def.elements || [];

  // Multi-step: collect pages
  const pages = elements.filter(e => e.type === 'page');
  const isMultiStep = settings.multiStep && pages.length > 0;
  const currentPageElems = isMultiStep ? (pages[step]?.elements || []) : elements;
  const totalSteps = isMultiStep ? pages.length : 1;

  const handleNext = () => {
    const errs = validateStep(currentPageElems);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handlePrev = () => { setStep(s => Math.max(0, s - 1)); window.scrollTo(0, 0); };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    let errs;
    try {
      errs = buildErrors(elements, values);
    } catch (err) {
      setErrors({ _submit: 'Validation error. Please check your answers and try again.' });
      console.error('buildErrors threw:', err);
      return;
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Scroll the first errored field into view
      const firstName = Object.keys(errs)[0];
      setTimeout(() => {
        const el = document.querySelector(`[name="${firstName}"], [data-field="${firstName}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSubmitting(true);
    try {
      const submitValues = cleanSubmitValues(elements, values);
      if (interactionParams) {
        // Interaction mode: resolve the paused workflow run via the Form Input node endpoint.
        const res = await fetch(`/human-interactions/${interactionParams.intId}/decide-form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: interactionParams.itok, fields: submitValues, form_source: 'xavvy_form' }),
        });
        if (res.ok) {
          setSubmitted(true);
        } else {
          const data = await res.json().catch(() => ({}));
          // data.detail may be a string or a structured object — always display a string
          const detail = data.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Submission failed. Please try again.');
          setErrors({ _submit: msg });
        }
      } else {
        // Normal xavvy form submission
        const res = await fetch(`/xf/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitValues),
        });
        if (res.ok) {
          localStorage.removeItem(`xf_${token}`);
          if (settings.redirectUrl) window.location.href = settings.redirectUrl;
          else setSubmitted(true);
        } else if (res.status === 422) {
          // Server-side rule set validation errors
          const data = await res.json().catch(() => ({}));
          const vErrors = data.validation_errors || [];
          if (vErrors.length > 0) {
            const fieldErrs = {};
            vErrors.forEach(ve => {
              if (ve.field) fieldErrs[ve.field] = ve.message || 'Invalid value';
              else fieldErrs._submit = (fieldErrs._submit ? fieldErrs._submit + ' ' : '') + (ve.message || 'Validation failed');
            });
            if (!fieldErrs._submit && Object.keys(fieldErrs).length === 0) {
              fieldErrs._submit = 'Server validation failed. Please review your answers.';
            }
            setErrors(fieldErrs);
            const firstName = Object.keys(fieldErrs).find(k => k !== '_submit');
            if (firstName) {
              setTimeout(() => {
                const el = document.querySelector(`[name="${firstName}"], [data-field="${firstName}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 50);
            }
          } else {
            const detail = data.detail;
            const msg = typeof detail === 'string' ? detail : 'Submission failed. Please try again.';
            setErrors({ _submit: msg });
          }
        } else {
          const data = await res.json().catch(() => ({}));
          const detail = data.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Submission failed. Please try again.');
          setErrors({ _submit: msg });
        }
      }
    } catch {
      setErrors({ _submit: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── CSS variables from theme ──────────────────────────────────────────────
  // Standalone: the creator's stored per-form colors (backgroundColor/
  // cardBackground/textColor) win so a published form keeps the branded look.
  // Embedded (inside the app, e.g. a CaseManager modal): ignore those stored
  // surface colors and always use the mode-driven slate palette, otherwise a
  // form saved with slate-dark text renders invisibly on a light host.
  // primaryColor/borderRadius/fontFamily are cosmetic and carry over in both
  // modes. Muted/border/input-bg are never overridable.
  const isLight = themeMode === 'light';
  const useCreatorSurfaces = !_isEmbeddedRenderer;
  const cssVars = {
    '--xf-primary': theme.primaryColor || '#6366f1',
    '--xf-bg': (useCreatorSurfaces && theme.backgroundColor) || (isLight ? '#f1f5f9' : '#0f172a'),
    '--xf-card': (useCreatorSurfaces && theme.cardBackground) || (isLight ? '#ffffff' : '#1e293b'),
    '--xf-text': (useCreatorSurfaces && theme.textColor) || (isLight ? '#0f172a' : '#f1f5f9'),
    '--xf-muted': isLight ? '#475569' : '#94a3b8',
    '--xf-border': isLight ? '#cbd5e1' : '#334155',
    '--xf-input-bg': isLight ? '#f8fafc' : 'rgba(15,23,42,0.8)',
    '--xf-error': '#ef4444',
    '--xf-radius': theme.borderRadius || '10px',
  };

  const embeddedMode = !!(definition || onValuesChange);
  const fullPageStyle = { minHeight: '100vh', background: 'var(--xf-bg)' };
  const loadBaseStyle = { ...cssVars, ...(embeddedMode ? {} : fullPageStyle), display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', fontSize: 14 };

  if (loading) return <div style={{ ...loadBaseStyle, color: 'var(--xf-muted)' }}>Loading…</div>;
  if (error) return <div style={{ ...loadBaseStyle, color: '#ef4444' }}>{error}</div>;
  if (!formData?.open && !embeddedMode) return <div style={{ ...loadBaseStyle, flexDirection: 'column', gap: 12 }}><div style={{ fontSize: 40 }}>🔒</div><div style={{ fontSize: 18, color: 'var(--xf-text)' }}>This form is currently closed</div></div>;

  if (pwProtected) return (
    <div style={{ ...cssVars, minHeight: '100vh', background: 'var(--xf-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: 20 }}>
      <div style={{ background: 'var(--xf-card)', borderRadius: 'var(--xf-radius)', padding: 32, width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--xf-text)', marginBottom: 6 }}>Password Required</div>
        <div style={{ color: 'var(--xf-muted)', fontSize: 13, marginBottom: 20 }}>Enter the password to access this form</div>
        <input type="password" value={pwInput} onChange={e => { setPwInput(e.target.value); setPwError(''); }} onKeyDown={e => e.key === 'Enter' && (() => { if (pwInput === settings.password) setPwProtected(false); else setPwError('Incorrect password'); })()} placeholder="Password" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 6, border: `1.5px solid ${pwError ? 'var(--xf-error)' : 'var(--xf-border)'}`, background: 'var(--xf-input-bg)', color: 'var(--xf-text)', fontSize: 14, marginBottom: 8, outline: 'none' }} />
        {pwError && <div style={{ color: 'var(--xf-error)', fontSize: 12, marginBottom: 8 }}>{pwError}</div>}
        <button onClick={() => { if (pwInput === settings.password) setPwProtected(false); else setPwError('Incorrect password'); }} style={{ width: '100%', padding: '11px', borderRadius: 6, border: 'none', background: 'var(--xf-primary)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Unlock</button>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...cssVars, minHeight: '100vh', background: 'var(--xf-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: 20 }}>
      <div style={{ background: 'var(--xf-card)', borderRadius: 'var(--xf-radius)', padding: 40, width: '100%', maxWidth: 480, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--xf-text)', marginBottom: 8 }}>
          {interactionParams ? 'Response recorded' : (settings.successMessage || 'Thank you!')}
        </div>
        <div style={{ color: 'var(--xf-muted)', fontSize: 14 }}>
          {interactionParams ? 'Your submission has been received and the workflow will continue.' : 'Your response has been recorded.'}
        </div>
      </div>
    </div>
  );

  // Inject custom CSS
  const customCssEl = settings.customCss ? <style>{settings.customCss}</style> : null;

  const isCard = theme.layout !== 'flat' && theme.layout !== 'minimal';
  const isEmbedded = !!(definition || onValuesChange);

  return (
    <RuleContext.Provider value={{ actorRoles: Array.isArray(actorRoles) ? actorRoles : [] }}>
    <div style={{ ...cssVars, ...(isEmbedded ? { fontFamily: theme.fontFamily || 'Inter, sans-serif', color: 'var(--xf-text)' } : { minHeight: '100vh', background: 'var(--xf-bg)', display: 'flex', justifyContent: 'center', padding: isCard ? '32px 16px' : 0, fontFamily: theme.fontFamily || 'Inter, sans-serif', color: 'var(--xf-text)' }) }}>
      {customCssEl}
      <div style={{ width: '100%', ...(isEmbedded ? {} : { maxWidth: 680, ...(isCard ? { background: 'var(--xf-card)', borderRadius: 'var(--xf-radius)', boxShadow: isLight ? '0 4px 28px rgba(15,23,42,0.08)' : '0 4px 40px rgba(0,0,0,0.4)', padding: '36px 40px' } : { padding: '24px 20px' }) }) }}>
        {/* Progress bar */}
        {isMultiStep && settings.showProgressBar && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--xf-muted)', marginBottom: 6 }}>
              {settings.showStepNumbers && <span>Step {step + 1} of {totalSteps}</span>}
              <span>{pages[step]?.label || ''}</span>
            </div>
            <div style={{ height: 4, background: 'var(--xf-border)', borderRadius: 2 }}>
              <div style={{ width: `${((step + 1) / totalSteps) * 100}%`, height: '100%', background: 'var(--xf-primary)', borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {/* Title — hidden when embedded (onValuesChange or readOnly with definition) */}
        {settings.title && step === 0 && !onValuesChange && !(readOnly && definition) && (
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 6px', color: 'var(--xf-text)' }}>{settings.title}</h1>
            {settings.description && <p style={{ margin: 0, color: 'var(--xf-muted)', fontSize: 14, lineHeight: 1.5 }}>{settings.description}</p>}
          </div>
        )}

        <form noValidate>
          {currentPageElems.map(elem => (
            <RenderElement key={elem.id} elem={elem} values={values} onChange={handleChange} errors={errors} allValues={values} displayOnly={readOnly && isEmbedded} />
          ))}

          {!readOnly && !onValuesChange && errors._submit && <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13 }}>{errors._submit}</div>}

          {!readOnly && !onValuesChange && (
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: isMultiStep && step > 0 ? 'space-between' : 'flex-end' }}>
              {isMultiStep && step > 0 && (
                <button type="button" onClick={handlePrev} style={{ padding: '11px 24px', borderRadius: 6, border: '1.5px solid var(--xf-border)', background: 'none', color: 'var(--xf-muted)', fontSize: 14, cursor: 'pointer' }}>← Previous</button>
              )}
              {isMultiStep && step < totalSteps - 1 ? (
                <button type="button" onClick={handleNext} style={{ padding: '11px 28px', borderRadius: 6, border: 'none', background: 'var(--xf-primary)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Next →</button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting} style={{ padding: '11px 28px', borderRadius: 6, border: 'none', background: 'var(--xf-primary)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Submitting…' : (settings.submitLabel || 'Submit')}
                </button>
              )}
            </div>
          )}

          {!readOnly && !onValuesChange && settings.allowDraftSave && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={() => { try { localStorage.setItem(`xf_draft_${token}`, JSON.stringify(values)); alert('Draft saved!'); } catch {} }} style={{ background: 'none', border: 'none', color: 'var(--xf-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Save draft</button>
            </div>
          )}
        </form>
      </div>
    </div>
    </RuleContext.Provider>
  );
}
